import React from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Rocket, Building2, GraduationCap, Users, Sparkles, MessageCircle, ExternalLink, Camera, Ticket } from "lucide-react";

const PILLARS = [
  { icon: Ticket, t: "Fans", d: "Discover, book and walk in with a QR ticket. Then get every photo of yourself from the night.", tile: "tile tile-lime" },
  { icon: Rocket, t: "Organisers", d: "List, promote, sell and check in — plus artists, vendors and sponsors on tap.", tile: "tile" },
  { icon: Building2, t: "Brands", d: "Sponsor real crowds and measure what actually happened on ground.", tile: "tile tile-magenta" },
  { icon: Users, t: "Ecosystem", d: "Artists, crews, venues and creators get discovered and booked.", tile: "tile tile-cyan" },
];

export default function AboutUs() {
  return (
    <Layout>
      <section className="relative overflow-hidden">
        <div className="blob blob-lg blob-purple blob-float" style={{ top: "-220px", left: "-120px" }} />
        <div className="blob blob-md blob-cyan blob-float delay-2" style={{ top: "120px", right: "-80px" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="chip !text-lime !border-lime/40 mb-5">About Eventom</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92]">
              We're building the <span className="grad-text">live culture</span> of Bharat.
            </h1>
            <p className="mt-6 text-base md:text-lg text-text/85 max-w-xl">
              Eventom Online started with one stubborn belief — a night out should not fall apart because of
              spreadsheets, screenshots and missing photos. So we built one place where fans, organisers, brands
              and the crews behind the scenes all meet.
            </p>
            <p className="mt-4 text-sm text-muted max-w-xl">
              Today Eventom runs discovery, ticketing, QR entry, AI event photos, sponsorships and a verified
              partner ecosystem — city by city, starting from tier-2 India where the energy actually is.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/organisers" className="btn-lime" data-testid="about-cta-organisers"><Rocket className="w-4 h-4" /> List your event</Link>
              <Link to="/work-with-eventom" className="btn-ghost" data-testid="about-cta-work"><Sparkles className="w-4 h-4" /> Work with Eventom</Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="genie-glow rounded-[28px] p-1">
              <div className="rounded-[26px] bg-surface/70 backdrop-blur-xl p-5">
                <img src="/genie-hero.png" alt="Eventom Genie — the founder" className="w-full max-h-[420px] object-contain genie-float" data-testid="about-genie-art" />
                <div className="genie-bubble mt-2">
                  <span className="font-script text-xl">Hi! I'm Genie</span>
                  <div className="text-xs text-white/70 mt-1">Your AI for events & experiences — built by the founder, trained on the whole Eventom circuit.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-skyx mb-2">What we connect</div>
        <h2 className="font-display text-4xl md:text-5xl ribbon-line">Four sides. One platform.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {PILLARS.map((p, i) => (
            <div key={p.t} className={`glass p-6 card-lift reveal d${i + 1}`} data-testid={`about-pillar-${p.t.toLowerCase()}`}>
              <span className={`${p.tile} w-14 h-14`}><p.icon className="w-6 h-6 relative z-10" /></span>
              <div className="font-display text-3xl mt-4">{p.t}</div>
              <p className="text-sm text-text/75 mt-2">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section-purple py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-lime mb-2">How we work</div>
          <h2 className="font-display text-4xl md:text-5xl mb-10">Honest by design.</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              ["QR-verified entry", "Every ticket is scanned at the gate. No screenshots, no duplicate entries."],
              ["Photos, not folders", "AI finds every photo of you from the event. One selfie, seconds later."],
              ["Real numbers only", "Organisers and brands see what happened — footfall, sales, pickup. Nothing inflated."],
            ].map(([t, d], i) => (
              <div key={t} className="glass p-6" data-testid={`about-value-${i}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-lime">0{i + 1}</div>
                <div className="font-display text-2xl mt-1">{t}</div>
                <p className="text-sm text-text/75 mt-2">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid md:grid-cols-2 gap-5">
          <a href="https://www.eventom.in" target="_blank" rel="noreferrer" data-testid="about-corporate-site" className="card-purple p-7 card-lift block">
            <span className="tile w-14 h-14"><Building2 className="w-6 h-6 relative z-10" /></span>
            <div className="font-display text-3xl mt-4">Corporate Website <ExternalLink className="inline w-5 h-5 mb-1" /></div>
            <p className="text-sm text-white/80 mt-2">Eventom Corptech — the company, the services arm, the enterprise work.</p>
            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-lime">eventom.in →</div>
          </a>
          <a href="https://www.eventom.live" target="_blank" rel="noreferrer" data-testid="about-college-fests" className="card-lime p-7 card-lift block">
            <span className="tile tile-lime w-14 h-14"><GraduationCap className="w-6 h-6 relative z-10" /></span>
            <div className="font-display text-3xl mt-4">College Fests <ExternalLink className="inline w-5 h-5 mb-1" /></div>
            <p className="text-sm text-white/80 mt-2">Eventom Live — the campus circuit: fests, pro-nights and inter-college competitions.</p>
            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-lime">eventom.live →</div>
          </a>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20 text-center">
        <span className="tile tile-cyan w-16 h-16 mx-auto"><Camera className="w-7 h-7 relative z-10" /></span>
        <h2 className="font-display text-4xl md:text-5xl mt-5">Come build the night with us.</h2>
        <p className="text-sm text-muted mt-3 max-w-lg mx-auto">Partner, perform, sponsor or join the team — every good event on Eventom started with one message.</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <a href="https://wa.me/917250120666?text=Hi%20Eventom!" target="_blank" rel="noreferrer" className="btn-lime" data-testid="about-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp us</a>
          <Link to="/ecosystem" className="btn-ghost" data-testid="about-cta-ecosystem"><Users className="w-4 h-4" /> Join the ecosystem</Link>
        </div>
      </section>
    </Layout>
  );
}
